import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { startAuthentication } from '@simplewebauthn/browser';
import { api } from '../api/client';
import { useAuth } from '../api/useAuth';
import { Stack, Row, Text, Card, Button, Input, Divider, Logo } from '../ui';

type Mode = 'signin' | 'signup' | 'passkey';

export function Login() {
  const { setFriendDirect } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState<Mode>(params.get('invite') ? 'signup' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [token] = useState(params.get('invite') ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const e = params.get('error');
    if (e === 'magic-expired') setError('that sign-in link expired or was already used.');
  }, [params]);

  function go(needsOnboarding: boolean, friend: any) {
    // Set state synchronously then navigate. The route guard now sees the friend.
    setFriendDirect(friend, needsOnboarding);
    // Tiny defer so React commits before nav (belt-and-suspenders)
    setTimeout(() => nav(needsOnboarding ? '/onboarding' : '/', { replace: true }), 0);
  }

  async function doSignup() {
    setBusy(true); setError(null);
    try {
      const r = await api.signup(email.trim(), password, name.trim(), token || undefined);
      go(r.needsOnboarding, r.friend);
    } catch (e: any) { setError(prettyError(e.message)); setBusy(false); }
  }

  async function doSignin() {
    setBusy(true); setError(null);
    try {
      const r = await api.signin(email.trim(), password);
      go(r.needsOnboarding, r.friend);
    } catch (e: any) { setError(prettyError(e.message)); setBusy(false); }
  }

  async function doPasskey() {
    setBusy(true); setError(null);
    try {
      const opts = await api.loginBegin(email.trim()) as any;
      const response = await startAuthentication({ optionsJSON: opts });
      const r: any = await api.loginFinish(email.trim(), response);
      // loginFinish returns { ok, friend } with no needsOnboarding flag — assume false
      go(false, r.friend);
    } catch (e: any) {
      setError(e.message?.includes('no-passkey-registered')
        ? 'no passkey registered for this email yet. sign in with your password instead.'
        : prettyError(e.message));
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-3">
      <Card className="w-full max-w-[440px]">
        <Stack gap={3}>
          <Stack gap={1}>
            <Row gap={2} align="center"><Logo size={28} /><Text variant="title">inner circle</Text></Row>
            <Text muted variant="small">{mode === 'signup' ? 'create your account' : mode === 'passkey' ? 'sign in with passkey' : 'sign in to your account'}</Text>
          </Stack>

          {mode === 'signup' && (
            <Stack gap={2}>
              {token && <div className="hairline rounded-sm bg-whisper px-2 py-1.5">
                <Text variant="label">invitation</Text>
                <Text variant="small" mono>{token.slice(0, 8)}…{token.slice(-4)}</Text>
              </div>}
              <Stack gap={0.5}><Text variant="label">your name</Text><Input value={name} onChange={e => setName(e.target.value)} placeholder="full name" autoFocus /></Stack>
              <Stack gap={0.5}><Text variant="label">email</Text><Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@yours.com" /></Stack>
              <Stack gap={0.5}>
                <Text variant="label">password</Text>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="at least 12 characters, mixed case + digit" onKeyDown={e => e.key === 'Enter' && doSignup()} />
              </Stack>
              <Button variant="primary" onClick={doSignup} disabled={busy || !email || password.length < 12 || !name}>{busy ? 'creating…' : 'create account'}</Button>
              <button className="text-xs text-muted hover:text-ink" onClick={() => { setMode('signin'); setError(null); }}>already have an account? sign in</button>
            </Stack>
          )}

          {mode === 'signin' && (
            <Stack gap={2}>
              <Stack gap={0.5}><Text variant="label">email</Text><Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@yours.com" autoFocus /></Stack>
              <Stack gap={0.5}><Text variant="label">password</Text><Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="your password" onKeyDown={e => e.key === 'Enter' && doSignin()} /></Stack>
              <Button variant="primary" onClick={doSignin} disabled={busy || !email || !password}>{busy ? 'signing in…' : 'sign in'}</Button>
              <Divider />
              <Row gap={1} justify="between">
                <button className="text-xs text-muted hover:text-ink" onClick={() => { setMode('signup'); setError(null); }}>create account</button>
                <button className="text-xs text-muted hover:text-ink" onClick={() => { setMode('passkey'); setError(null); }}>use passkey instead</button>
              </Row>
            </Stack>
          )}

          {mode === 'passkey' && (
            <Stack gap={2}>
              <Stack gap={0.5}><Text variant="label">email</Text><Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@yours.com" autoFocus /></Stack>
              <Button variant="primary" onClick={doPasskey} disabled={busy || !email}>{busy ? 'signing in…' : 'sign in with passkey'}</Button>
              <button className="text-xs text-muted hover:text-ink" onClick={() => { setMode('signin'); setError(null); }}>back to password sign-in</button>
            </Stack>
          )}

          {error && <Text variant="small" className="text-negative">{error}</Text>}
        </Stack>
      </Card>
    </div>
  );
}

function prettyError(raw: string) {
  if (!raw) return 'something went wrong';
  if (raw.startsWith('400:') || raw.startsWith('401:') || raw.startsWith('403:') || raw.startsWith('409:')) {
    try {
      const idx = raw.indexOf('{');
      if (idx > -1) {
        const j = JSON.parse(raw.slice(idx));
        return j.error ?? raw;
      }
    } catch {}
  }
  return raw;
}
